import ID from '@/models/ID'
import Initiative from '@/models/Initiative'
import InitiativeChannel from '@/models/InitiativeChannel'
import InitiativeCustomization from '@/models/InitiativeCustomization'
import InitiativeType from '@/models/InitiativeType'
import axios from 'axios'
import { getApiRangeEndBound, getApiRangeStartBound } from '@/utils/apiDateBounds'

const INITIATIVES_PATH = 'initiatives'
const INITIATIVE_TYPES_PATH = 'initiative_types'
const INITIATIVE_CHANNELS_PATH = 'initiative_channels'
const INITIATIVE_CUSTOMIZATIONS_PATH = 'initiative_customizations'
const INITIATIVE_SUMMARIES_PATH = 'initiative_summaries'
const INITIATIVE_CUSTOMIZATION_SUMMARIES_PATH = 'initiative_customization_summaries'

export default class InitiativesService {
  public create(newInitiative: Initiative) {
    return new Promise((resolve, reject) => {
      axios
        .post(INITIATIVES_PATH, { ...newInitiative.forRequestObject(), id: null })
        .then(
          (response) => {
            resolve(Initiative.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public get(ids: number[]) {
    return new Promise((resolve, reject) => {
      axios.get(`${INITIATIVES_PATH}/${ids.toString()}`).then(
        (response) => {
          ids.length !== 0
            ? resolve(Initiative.fromResponseObject(response.data))
            : resolve(
              response.data.map((initiative) => Initiative.fromResponseObject(initiative))
            )
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public fetchInitiativesForPlanInDateRange(planIds: number[], dateRange: Date[]) {
    // ID options:
      // Single: plan.id=123
      // Array: plan.id[]=123&plan.id[]=124

    // Date filter options:
      // startDate[after]=
      // startDate[before]=
      // startDate[strictly_after]= // Excludes initiatives that start on the specified date
      // startDate[strictly_before]= // Excludes initiatives that end on the specified date
      // endDate[after]=
      // endDate[before]=
      // endDate[strictly_after]= // Excludes initiatives that start on the specified date
      // endDate[strictly_before]= // Excludes initiatives that end on the specified date

    // Example: /api/initiatives?plan.id[]=86&plan.id[]=87&plan.id[]=88&startDate[after]=2025-01-01&endDate[before]=2025-12-31
    
    let requestString = ''
    if(planIds.length === 1){
      requestString = `${INITIATIVES_PATH}?plan.id=${planIds.toString()}`
    }
    if (planIds.length > 1) {
      requestString = `${INITIATIVES_PATH}?plan.id[]=${planIds.join('&plan.id[]=')}`
    }
    requestString += `&startDate[before]=${getApiRangeEndBound(dateRange[1])}&endDate[after]=${getApiRangeStartBound(dateRange[0])}`

    return new Promise((resolve, reject) => {
      axios.get(requestString).then(
        (response) => {
          resolve(response.data['hydra:member'].map((initiative) => Initiative.fromResponseObject(initiative)))
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public update(updatedInitiative: Initiative, changedFields?: string[]) {
    return new Promise((resolve, reject) => {
      axios
        .put(`${INITIATIVES_PATH}/${updatedInitiative.id.intID}`, {
          ...updatedInitiative.forRequestObject(),
        })
        .then(
          (response) => {
            resolve(Initiative.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public delete(id: number) {
    return new Promise((resolve, reject) => {
      axios.delete(`${INITIATIVES_PATH}/${id}`).then(
        (response) => {
          resolve(response.data)
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public async fetchSummaryContent(contentUrl: string): Promise<string> {
    const client = axios.create()
    delete client.defaults.headers.common['Authorization']
    const response = await client.get(contentUrl)
    return response.data
  }

  public createInitiativeSummary(initiativeIRI: string, content: string) {
    return new Promise((resolve, reject) => {
      axios.post(INITIATIVE_SUMMARIES_PATH, { initiative: initiativeIRI, content }).then(
        (response) => {
          resolve({ id: ID.fromResponseObject(response.data['@id'], 'initiative_summaries'), contentUrl: response.data.contentUrl })
        },
        (error) => { reject(error) }
      )
    })
  }

  public updateInitiativeSummary(summaryId: number, content: string) {
    return new Promise((resolve, reject) => {
      axios.put(`${INITIATIVE_SUMMARIES_PATH}/${summaryId}`, { content }).then(
        (response) => {
          resolve({ id: ID.fromResponseObject(response.data['@id'], 'initiative_summaries'), contentUrl: response.data.contentUrl })
        },
        (error) => { reject(error) }
      )
    })
  }

  public createCustomizationSummary(customizationIRI: string, content: string) {
    return new Promise((resolve, reject) => {
      axios.post(INITIATIVE_CUSTOMIZATION_SUMMARIES_PATH, { initiativeCustomization: customizationIRI, content }).then(
        (response) => {
          resolve({ id: ID.fromResponseObject(response.data['@id'], 'initiative_customization_summaries'), contentUrl: response.data.contentUrl })
        },
        (error) => { reject(error) }
      )
    })
  }

  public updateCustomizationSummary(summaryId: number, content: string) {
    return new Promise((resolve, reject) => {
      axios.put(`${INITIATIVE_CUSTOMIZATION_SUMMARIES_PATH}/${summaryId}`, { content }).then(
        (response) => {
          resolve({ id: ID.fromResponseObject(response.data['@id'], 'initiative_customization_summaries'), contentUrl: response.data.contentUrl })
        },
        (error) => { reject(error) }
      )
    })
  }

  // InitiativeTypes
  public createInitiativeType(newType: InitiativeType) {
    return new Promise((resolve, reject) => {
      axios
        .post(INITIATIVE_TYPES_PATH, {...newType.forRequestObject(), id: null})
        .then(
          (response) => {
            resolve(InitiativeType.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public getInitiativeType(ids: number[]) {
    return new Promise((resolve, reject) => {
      axios.get(`${INITIATIVE_TYPES_PATH}/${ids.toString()}`).then(
        (response) => {
          ids.length !== 0
            ? resolve(InitiativeType.fromResponseObject(response.data))
            : resolve(
              response.data.map((initiativeType) => InitiativeType.fromResponseObject(initiativeType))
            )
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public updateInitiativeType(updatedType: InitiativeType) {
    return new Promise((resolve, reject) => {
      axios
        .put(`${INITIATIVE_TYPES_PATH}/${updatedType.id.intID}`, {
          ...updatedType.forRequestObject(),
        })
        .then(
          (response) => {
            resolve(InitiativeType.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public deleteInitiativeType(typeIntId: number) {
    return new Promise((resolve, reject) => {
      axios.delete(`${INITIATIVE_TYPES_PATH}/${typeIntId.toString()}`).then(
        (response) => {
          resolve(response)
        },
        (error) => {
          reject(error)
        }
      )
    })
  }


  // InitiativeChannels
  public getChannel(ids: number[]) {
    return new Promise((resolve, reject) => {
      axios.get(`${INITIATIVE_CHANNELS_PATH}/${ids.toString()}`).then(
        (response) => {
          resolve(
            response.data.map((channel) => InitiativeChannel.fromResponseObject(channel))
          )
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public updateChannel(updatedInitiativeChannel: InitiativeChannel, changedFields?: string[]) {
    return new Promise((resolve, reject) => {
      axios
        .put(`${INITIATIVE_CHANNELS_PATH}/${updatedInitiativeChannel.id.intID}`, {
          ...updatedInitiativeChannel.forRequestObject(),
        })
        .then(
          (response) => {
            resolve(InitiativeChannel.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public createCustomization(newCustomization: InitiativeCustomization) {
    return new Promise((resolve, reject) => {
      axios
        .post(INITIATIVE_CUSTOMIZATIONS_PATH, { ...newCustomization.forRequestObject(), id: null })
        .then(
          (response) => {
            resolve(InitiativeCustomization.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public getCustomization(ids: number[]) {
    return new Promise((resolve, reject) => {
      axios.get(`${INITIATIVE_CUSTOMIZATIONS_PATH}/${ids.toString()}`).then(
        (response) => {
          ids.length !== 0
            ? resolve(InitiativeCustomization.fromResponseObject(response.data))
            : resolve(
              response.data.map((customization) => InitiativeCustomization.fromResponseObject(customization))
            )
        },
        (error) => {
          reject(error)
        }
      )
    })
  }

  public updateCustomization(updatedCustomization: InitiativeCustomization, changedFields?: string[]) {
    return new Promise((resolve, reject) => {
      axios
        .put(`${INITIATIVE_CUSTOMIZATIONS_PATH}/${updatedCustomization.id.intID}`, {
          ...updatedCustomization.forRequestObject(),
        })
        .then(
          (response) => {
            resolve(InitiativeCustomization.fromResponseObject(response.data))
          },
          (error) => {
            reject(error)
          }
        )
    })
  }

  public deleteCustomization(id: number) {
    return new Promise((resolve, reject) => {
      axios.delete(`${INITIATIVE_CUSTOMIZATIONS_PATH}/${id}`).then(
        (response) => {
          resolve(response.data)
        },
        (error) => {
          reject(error)
        }
      )
    })
  }
}
